import React from "react";
import {
  ProgressBar as BaseUIProgressBar,
  ProgressBarRounded as BaseUIProgressBarRounded,
  ProgressBarProps as BaseProgressBarProps,
  ProgressBarRoundedProps,
  SIZE,
  ProgressBarOverrides,
} from "baseui/progress-bar";
import { Overrides } from "baseui/overrides";
import { mergeOverrides } from "baseui";
import { Theme } from "../../../../styling/baseui_theme";

const getLabelFontSize = ($theme: Theme, $size?: ProgressBarProps["size"]) => {
  switch ($size) {
    case SIZE.small:
      return $theme.typography.font200.fontSize;
    case SIZE.medium:
      return $theme.typography.font300.fontSize;
    case SIZE.large:
      return $theme.typography.font400.fontSize;
    default:
      return $theme.typography.font100.fontSize;
  }
};

const defaultOverrides: Overrides<ProgressBarOverrides> = {
  BarProgress: {
    style: ({ $theme }: { $theme: Theme }) => ({
      color: $theme.colors.accent,
    }),
  },
  Bar: {
    style: ({ $theme }: { $theme: Theme }) => ({
      backgroundColor: $theme.colors.progressbarTrackFill,
    }),
  },
  InfiniteBar: {
    style: ({ $theme }: { $theme: Theme }) => ({
      color: $theme.colors.accent,
    }),
  },
  Label: {
    style: ({ $theme, $size }: { $theme: Theme; $size?: ProgressBarProps["size"] }) => ({
      fontSize: getLabelFontSize($theme, $size),
    }),
  },
};

const roundedOverrides = {
  TrackForeground: {
    style: ({ $theme }: { $theme: Theme }) => ({
      stroke: $theme.colors.accent,
    }),
  },
  Text: {
    style: ({ $theme, $size }: { $theme: Theme; $size?: ProgressBarRoundedProps["size"] }) => ({
      fontSize: getLabelFontSize($theme, $size),
    }),
  },
};

export type ProgressBarProps = Omit<
  BaseProgressBarProps,
  "infinite" | "steps" | "successValue" | "maxValue" | "minValue"
> & {
  infinite?: boolean;
  steps?: number;
  styletronOverrides?: ProgressBarOverrides;
  arialLabel?: string;
  dataTest?: string;
};

export const ProgressBar = ({
  value,
  steps = 1,
  size = SIZE.medium,
  getProgressLabel,
  showLabel = false,
  infinite = false,
  styletronOverrides,
  ariaLabel,
  dataTest,
}: ProgressBarProps) => {
  let styleOverrides = defaultOverrides;

  if (styletronOverrides) {
    styleOverrides = mergeOverrides(
      styletronOverrides as Overrides<ProgressBarOverrides>,
      defaultOverrides,
    );
  }
  return (
    <BaseUIProgressBar
      value={value}
      steps={steps}
      size={size}
      getProgressLabel={getProgressLabel}
      showLabel={showLabel}
      infinite={infinite}
      overrides={styleOverrides}
      aria-label={ariaLabel}
      data-test={dataTest}
    />
  );
};

export const ProgressBarRounded = ({
  progress,
  size = SIZE.medium,
  animate = true,
  inline = false,
}: ProgressBarRoundedProps) => {
  return (
    <BaseUIProgressBarRounded
      progress={progress}
      size={size}
      animate={animate}
      inline={inline}
      overrides={roundedOverrides}
    />
  );
};
